import { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiChip } from "@miplatform/components";
import { Box, Typography } from "@mui/material";
import { ORDERS_DRAWER_WIDTH } from "../ordersConstants";

/**
 * OrderTemplatesFilter component.
 * Renders a filter section on the left side of a drawer.
 * Includes a trade selection dropdown and displays order types in cards with their counts.
 *
 * @param {Object} props - The component props.
 * @param {Array<string>} props.selectedOrderTemplate - Selected order template object.
 * @param {Function} props.onFilterChange - Callback for handling filter change.
 * @returns {JSX.Element} - Rendered OrderTemplatesFilter component.
 */
export const OrderTemplatesFilter = memo(
  ({
    filters: { selectedOrderTemplate },
    orderTemplates,
    orderTypeCount,
    onFilterChange,
  }) => {
    const { classes } = useStyles();
    const options = useMemo(
      () =>
        orderTemplates.map((orderType) => ({
          ...orderType,
          label: orderType.name,
          value: orderType.id,
        })),
      [orderTemplates]
    );

    const renderOrderTemplates = () => {
      const totalCount = Object.values(orderTypeCount || {}).reduce(
        (acc, curr) => acc + curr,
        0
      );

      const allItem = (
        <Box
          key="all"
          data-testid="order-template-filter-all"
          className={[
            classes.orderTypeContainer,
            !selectedOrderTemplate && classes.selectedOrderType,
          ]}
          onClick={() => onFilterChange({ orderTemplateId: null })}
        >
          <Box className={classes.orderLabel}>
            <Typography
              fontSize={14}
              variant="subtitle2"
              minHeight={14}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              fontWeight={600}
              maxWidth={150}
            >
              All
            </Typography>
            {totalCount > 0 ? (
              <MiChip
                label={totalCount}
                size="small"
                color="error"
                sx={{ height: 18, fontSize: 11 }}
              />
            ) : null}
          </Box>
        </Box>
      );

      return [
        allItem,
        ...options.map(({ id, label }) => (
          <Box
            key={id}
            data-testid={`order-template-filter-${label}`}
            className={[
              classes.orderTypeContainer,
              selectedOrderTemplate?.id === id && classes.selectedOrderType,
            ]}
            onClick={() =>
              onFilterChange({ orderTemplateId: id, orderTemplateLabel: label })
            }
          >
            <Box className={classes.orderLabel}>
              <Typography
                fontSize={14}
                variant="subtitle2"
                minHeight={14}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                fontWeight={600}
                maxWidth={150}
              >
                {label}
              </Typography>
              {orderTypeCount?.[id] ? (
                <MiChip
                  label={orderTypeCount[id]}
                  size="small"
                  color="error"
                  sx={{ height: 18, fontSize: 11 }}
                />
              ) : null}
            </Box>
          </Box>
        )),
      ];
    };

    return (
      <Box className={classes.container} data-testid="order-templates-filter">
        <Box className={classes.selectedTradeContainer}>
          <Typography variant="body1" color="primary.main" fontWeight={600}>
            Order Types
          </Typography>
        </Box>
        {orderTemplates.length ? (
          renderOrderTemplates()
        ) : (
          <Typography className={classes.emptyText} color="grey">
            No order types available
          </Typography>
        )}
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  container: {
    width: ORDERS_DRAWER_WIDTH,
    border: "1px solid transparent",
    display: "flex",
    overflow: "scroll",
    flexDirection: "column",
    backgroundColor: theme.palette.common.white,
    paddingBottom: theme.spacing(1),
  },
  tradeField: {
    margin: theme.spacing(0, 2),
  },
  selectedTradeContainer: {
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(2, 4),
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey.lightAccent} `,
  },
  orderTypeContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2),
    margin: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(
      ["background-color", "margin", "border-radius", "box-shadow"],
      {
        duration: theme.transitions.duration.standard,
      }
    ),
    ":hover": {
      backgroundColor: theme.palette.grey.lightAccent,
    },
    cursor: "pointer",
  },
  selectedOrderType: {
    backgroundColor: theme.palette.grey.accent,
    position: "relative",
    zIndex: 1,
    boxShadow: `0 0 0 1px ${theme.palette.action.disabled}, ${theme.shadows[1]}`,
    "&:hover": {
      boxShadow: `0 0 0 1px ${theme.palette.action.active}, ${theme.shadows[2]}`,
    },
  },
  outlinedBox: {
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(["border-color", "box-shadow"], {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover": {
      borderColor: theme.palette.action.active,
    },
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  orderLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  emptyText: {
    padding: theme.spacing(2, 2),
  },
}));
